import { FC, useEffect, useState } from 'react'
import {
  Body1,
  Box,
  Chip,
  ClassNameMap,
  findBy,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@perk-ui/core'
import clsx from 'clsx'
import { endOfDay, isFuture, isPast, parseISO } from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'
import { Calendar, Check, Clock, Loader, XSquare } from 'react-feather'

import { useSitesContext } from '../../../components/SitesProvider'
import {
  PGII_SCORE,
  SATISFACTION_PSC,
  SATISFACTION_SCORE,
  UROLOGY_CONDITION,
} from '../../../config/constants'
import { templateTheme as theme } from '../../../config/theme'
import {
  SurveyResponse,
  SurveyResponseScore,
} from '../../../features/query-hooks/useSurveyResponse'
// import SurveyStatusChip from './SurveyStatusChip'
import { SurveyResponseSearchResponse } from '../../../features/query-hooks/useSurveyResponseSearch'
import useDates from '../../../hooks/useDates'
import SurveyAnswersModal from './SurveyAnswersModal'

export interface SurveyTableProps {
  surveyPeriods?: string[]
  surveys?: SurveyResponseSearchResponse
  timezone: string
}

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    // border: '1px solid rgb(224,224,224)',
    maxWidth: 'calc(100vw - 380px)',
  },
  table: {
    tableLayout: 'fixed',
    minWidth: '100%',
    borderSpacing: '5px',
  },
  tableHeader: {
    backgroundColor: theme.palette.common.white,
    textTransform: 'capitalize',
    textAlign: 'center',
  },
  tableHeaderIcon: {
    display: 'flex',
    fontWeight: 'bold',
    backgroundColor: theme.palette.common.white,
    justifyContent: 'flex-start',
  },
  contentRow: {},
  cellContent: {
    padding: theme.spacing(1),
    width: 150,
    height: 70,
    textAlign: 'center',
    border: '1px solid #F1F1F1',
    borderRadius: 6,
    backgroundColor: '#FAFAFB',
  },
  clickablecellContent: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#F1F1F1',
    },
    '&:active': {
      backgroundColor: '#F1F1F1',
    },
    '&:focus': {
      backgroundColor: '#F1F1F1',
    },
  },
  noBordercellContent: {
    borderBottom: 'none',
  },
  missingInfo: {
    color: theme.palette.error.main,
    '&:hover': {
      backgroundColor: '#F9E6E6',
    },
    '&:active': {
      backgroundColor: '#F9E6E6',
    },
    '&:focus': {
      backgroundColor: '#F9E6E6',
    },
  },
  cellData: {
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      marginRight: '2px',
    },
  },
  archivedRow: {
    color: '#A3A3A3',
  },
  archivedCell: {
    color: '#A3A3A3',
  },
  archivedStatusIcon: {
    borderRadius: '25px',
    padding: '2px 8px',
    color: '#919191',
    backgroundColor: '#D5D5D5',
    marginLeft: theme.spacing(1),
    display: 'flex',
    '& svg': {
      margin: 0,
    },
  },
  chipStatusComplete: {
    backgroundColor: '#E6F6E6',
    borderRadius: '6px',
    width: '100%',
    color: '#00A800',
    fontSize: '11px',
    '& .MuiChip-icon': {
      color: theme.palette.common.white,
      backgroundColor: '#00A800',
      borderRadius: '8px',
      padding: '2px',
    },
  },
  chipStatusOpen: {
    backgroundColor: '#FEF5ED',
    borderRadius: '6px',
    width: '100%',
    color: '#F79A48',
    fontSize: '11px',
    '& .MuiChip-icon': {
      color: theme.palette.common.white,
      backgroundColor: '#F79A48',
      borderRadius: '8px',
      padding: '2px',
    },
  },
  chipStatusExpired: {
    backgroundColor: '#F1F1F1',
    borderRadius: '6px',
    width: '100%',
    color: '#B9B9B9',
    fontSize: '11px',
    '& .MuiChip-icon': {
      color: theme.palette.common.white,
      backgroundColor: '#B9B9B9',
      borderRadius: '8px',
      padding: '2px',
    },
  },
  chipStatusNotOpen: {
    backgroundColor: '#EAF2F5',
    borderRadius: '6px',
    width: '100%',
    color: '#2E8099',
    fontSize: '11px',
    '& .MuiChip-icon': {
      color: theme.palette.common.white,
      backgroundColor: '#2E8099',
      borderRadius: '8px',
      padding: '2px',
    },
  },
  sticky: {
    position: 'sticky',
    left: 0,
    zIndex: 3,
  },
  scoreCell: {
    fontWeight: 700,
  },
  expiredSurvey: {
    opacity: 0.5,
  },
}))

export const statusProperties = {
  completed: {
    scheduleLabel: 'Completed',
    label: 'Not open',
    color: theme.palette.common.black,
    fontWeight: 700,
  },
  'not-open': {
    scheduleLabel: 'Opens',
    label: '',
    color: theme.palette.common.black,
    fontWeight: 700,
  },
  open: {
    scheduleLabel: 'Due',
    label: 'Open',
    color: theme.palette.success.main,
    fontWeight: 700,
  },
  expired: {
    scheduleLabel: 'Expired',
    label: '',
    color: theme.palette.error.main,
    fontWeight: 700,
  },
  'N/A': {
    scheduleLabel: 'N/A',
    label: '-',
    color: theme.palette.error.main,
    fontWeight: 700,
  },
}

export const getSurveyStatus = (
  scores: SurveyResponseScore[],
  availableOn: Date,
  expiresOn: Date,
  timeZone: string,
  survey: SurveyResponse,
) => {
  const score = scores[0]

  if (score) {
    return 'completed'
  }
  if (
    survey.survey.slug === 'AE-PATIENT' ||
    survey.survey.slug === 'SATISFACTION-SCORE' ||
    survey.survey.slug === 'REVIEW-ROUTING' ||
    survey.survey.slug === 'SATISFACTION-PSC' ||
    survey.survey.slug === 'VIVEX-ADDITIONAL-TREATMENT' ||
    survey.survey.slug === 'VIVEX-BACK-PAIN-MEDICATION' ||
    survey.survey.slug === 'PHYSICAL-THERAPY' ||
    survey.survey.slug === 'SEXUALLY-ACTIVE'
  ) {
    if (survey.completedAt) {
      return 'completed'
    }
  }
  const status = isFuture(zonedTimeToUtc(availableOn, timeZone))
    ? 'not-open'
    : isPast(zonedTimeToUtc(endOfDay(expiresOn), timeZone))
    ? 'expired'
    : 'open'

  return status
}

export const getSurveyScore = (
  surveyStatus: string,
  survey: SurveyResponse,
  status: string,
  showName?: boolean,
) => {
  let score = status as string | number
  let scoreName = ''
  if (surveyStatus === 'completed') {
    scoreName =
      findBy('isPrimary', true, survey?.surveyResponseScores)?.name || ''
    if (survey.survey.slug === 'AE-PATIENT') {
      const aeScore = survey.surveyResponseAnswers[0].values[0]
      score = aeScore === '1.0' ? 'Yes' : 'No'
    } else if (survey.survey.slug === 'SEXUALLY-ACTIVE') {
      const aeScore = survey.surveyResponseAnswers[0].values[0]
      score = aeScore === '1.0' ? 'Yes' : 'No'
    } else if (survey.survey.slug === 'SATISFACTION-SCORE') {
      const satisfactionScore = SATISFACTION_SCORE.find(
        (ss) => ss.value === survey.surveyResponseAnswers[0].values[0],
      )
      score = satisfactionScore
        ? satisfactionScore.slug
        : survey.surveyResponseAnswers[0].values[0]
    } else if (survey.survey.slug === 'REVIEW-ROUTING') {
      const satisfactionAnswer = survey.surveyResponseAnswers.find(
        (a) => a.surveyQuestion.slug === 'REVIEW-ROUTING-SATISFACTION-Q',
      )
      const answerValue = satisfactionAnswer
        ? satisfactionAnswer.values[0]
        : survey.surveyResponseAnswers[survey.surveyResponseAnswers.length - 1]
            .values[0]

      const satisfactionScore = SATISFACTION_SCORE.find(
        (ss) => ss.value === answerValue,
      )
      score = satisfactionScore
        ? satisfactionScore.slug
        : survey.surveyResponseAnswers[0].values[0]
    } else if (survey.survey.slug === 'SATISFACTION-PSC') {
      const satisfactionScore = SATISFACTION_PSC.find(
        (ss) => ss.value === survey.surveyResponseAnswers[0].values[0],
      )
      score = satisfactionScore
        ? satisfactionScore.slug
        : survey.surveyResponseAnswers[0].values[0]
    } else if (survey.survey.slug === 'PGI-I') {
      const pgiiScore = PGII_SCORE.find(
        (pgii) => pgii.value === survey.surveyResponseAnswers[0].values[0],
      )
      score = pgiiScore
        ? pgiiScore.slug
        : survey.surveyResponseAnswers[0].values[0]
    } else if (survey.survey.slug === 'VIVEX-ADDITIONAL-TREATMENT') {
      const vivexATScore = survey.surveyResponseAnswers[0].values[0]
      score =
        vivexATScore === '1.0'
          ? 'Taking Additional Medication'
          : 'Not Taking Additional Medication'
    } else if (survey.survey.slug === 'VIVEX-BACK-PAIN-MEDICATION') {
      const vivexBPMScore = survey.surveyResponseAnswers[0].values[0]
      score =
        vivexBPMScore === '1.0'
          ? 'Taking Pain Medications'
          : 'Not Taking Pain Medications'
    } else if (survey.survey.slug === 'BDI') {
      const bdiScore = +Number(
        (findBy('isPrimary', true, survey?.surveyResponseScores) || {}).value,
      ).toFixed(1)
      score = `${bdiScore} (${bdiScoreLabel(parseInt(bdiScore.toString()))})`
    } else if (survey.survey.slug === 'IIEF-5') {
      const iief5score = +Number(
        (findBy('isPrimary', true, survey?.surveyResponseScores) || {}).value,
      ).toFixed(1)
      score = `${iief5score} (${iief5ScoreLabel(
        parseInt(iief5score.toString()),
      )})`
    } else if (survey.survey.slug === 'PHYSICAL-THERAPY') {
      const aeScore = survey.surveyResponseAnswers[0].values[0]
      score = aeScore === '1.0' ? 'Yes' : 'No'
    } else if (survey.survey.slug === 'UROLOGY') {
      const urologyScore = survey.surveyResponseAnswers[0].values[0]
      score =
        UROLOGY_CONDITION.find((condition) => condition.value === urologyScore)
          ?.slug || urologyScore
    } else {
      score = +Number(
        (findBy('isPrimary', true, survey?.surveyResponseScores) || {}).value,
      ).toFixed(1)
    }
  }
  if (surveyStatus === 'open') {
    return <Loader size={14} color={'#B9B9B9'} />
  }
  if (showName && survey?.survey.slug === 'NUMERIC-PAIN-RATING')
    return `${scoreName}: ${score}`
  return score
}

const bdiScoreLabel = (value: number) => {
  if (value > 40) {
    return 'Extreme depression'
  } else if (value > 30 && value <= 40) {
    return 'Severe depression'
  } else if (value > 20 && value <= 30) {
    return 'Moderate depression'
  } else if (value > 16 && value <= 20) {
    return 'Borderline clinical depression'
  } else if (value > 10 && value <= 16) {
    return 'Mild mood disturbance'
  } else {
    return 'These ups and downs are considered normal'
  }
}

const iief5ScoreLabel = (value: number) => {
  if (value > 21) {
    return 'No erectile dysfunction'
  } else if (value > 16 && value <= 21) {
    return 'Mild erectile dysfunction'
  } else if (value > 11 && value <= 16) {
    return 'Mild to moderate erectile dysfunction'
  } else if (value > 7 && value <= 11) {
    return 'Moderate erectile dysfunction'
  } else if (value > 4 && value <= 7) {
    return 'Severe erectile dysfunction'
  } else {
    return 'These ups and downs are considered normal'
  }
}

const SurveyTable: FC<SurveyTableProps> = ({
  surveyPeriods,
  surveys,
  timezone,
}) => {
  const classes = useStyles()
  const { currentSite } = useSitesContext()
  const { localizedFormatDate } = useDates()
  const [tBodyRows, setTBodyRows] = useState<Array<JSX.Element>>([])
  const [openAnswersModal, setOpenAnswerModal] = useState<boolean>(false)
  const [modalAnswers, setModalAnswers] = useState<SurveyResponse | null>(null)
  const [isGroupView, setIsGroupView] = useState<boolean>(false)

  const surveysGroupedBySlug = surveys?.surveyResponses?.reduce((acc, item) => {
    const surveySlug = item.survey.slug

    if (surveySlug && item?.survey?.slug !== 'TREATMENT-HISTORY') {
      acc[surveySlug] = acc[surveySlug] || []
      acc[surveySlug].push(item)
    }
    return acc
  }, Object.create(null))
  const surveysGroupedBySchedule = surveys?.surveyResponses?.reduce(
    (acc, item) => {
      if (Object.keys(item?.metadata).length === 0) return acc

      const itemSchedule = findBy('type', 'schedule', item.metadata)

      if (itemSchedule && item?.survey?.slug !== 'TREATMENT-HISTORY') {
        acc[itemSchedule.value] = acc[itemSchedule.value] || []
        acc[itemSchedule.value].push(item)
      }
      return acc
    },
    Object.create(null),
  )
  const getSurveyGroupStatus = (
    classes: ClassNameMap,
    surveysGroupedByPeriod: SurveyResponse[],
    timezone: string,
  ) => {
    const periodArrayStatus = surveysGroupedByPeriod.map((survey) => {
      return getSurveyStatus(
        survey.surveyResponseScores,
        parseISO(survey.availableOn),
        parseISO(survey.expiresOn),
        timezone,
        survey,
      )
    })
    if (periodArrayStatus.every((status) => status === 'completed')) {
      return (
        <Chip
          className={classes.chipStatusComplete}
          icon={<Check size={16} />}
          label="completed"
        />
      )
    } else if (periodArrayStatus.includes('open')) {
      return (
        <Chip
          className={classes.chipStatusOpen}
          icon={<Clock size={16} />}
          label="open"
          onClick={() => {
            setModalAnswers(surveysGroupedByPeriod[0])
            setOpenAnswerModal(true)
            setIsGroupView(true)
          }}
        />
      )
    } else if (periodArrayStatus.includes('expired')) {
      return (
        <Chip
          className={classes.chipStatusExpired}
          icon={<XSquare size={16} />}
          label="Expired"
        />
      )
    } else if (periodArrayStatus.includes('not-open')) {
      return (
        <Chip
          className={classes.chipStatusNotOpen}
          icon={<Calendar size={16} />}
          label={localizedFormatDate(
            parseISO(surveysGroupedByPeriod[0].availableOn),
          )}
        />
      )
    }
    return
  }
  useEffect(() => {
    if (tBodyRows.length === 0) loadTableBody()
  }, [surveys])

  const loadTableBody = () => {
    const tableRows: JSX.Element[] = []
    Object.keys(surveysGroupedBySlug || {})?.map((surveySlug: string) => {
      const scoreName = surveysGroupedBySlug[surveySlug][0]?.survey?.name
      tableRows.push(
        <TableRow key={surveySlug} className={classes.contentRow}>
          <TableCell
            key={'nameHeader'}
            className={clsx(classes.cellContent, classes.sticky)}
          >
            {scoreName}
          </TableCell>
          {surveyPeriods?.map((period) => {
            const currentPeriodSurvey = surveysGroupedBySlug[surveySlug]?.find(
              (surveyResponse: SurveyResponse) => {
                return (
                  surveyResponse.metadata.find(
                    (meta) => meta.type === 'schedule',
                  )?.value === period
                )
              },
            ) as SurveyResponse

            const surveyStatus = currentPeriodSurvey
              ? getSurveyStatus(
                  currentPeriodSurvey.surveyResponseScores,
                  parseISO(currentPeriodSurvey.availableOn),
                  parseISO(currentPeriodSurvey.expiresOn),
                  timezone,
                  currentPeriodSurvey,
                )
              : 'N/A'

            return (
              <TableCell
                key={period}
                className={clsx(classes.cellContent, classes.scoreCell, {
                  [classes.clickablecellContent]: surveyStatus === 'completed',
                  [classes.expiredSurvey]: surveyStatus === 'expired',
                })}
                onClick={() => {
                  if (surveyStatus === 'completed') {
                    setModalAnswers(currentPeriodSurvey)
                    setOpenAnswerModal(true)
                    setIsGroupView(false)
                  }
                }}
              >
                {getSurveyScore(
                  surveyStatus,
                  currentPeriodSurvey,
                  statusProperties[surveyStatus].label,
                )}
              </TableCell>
            )
          })}
        </TableRow>,
      )
    })
    setTBodyRows(tableRows || [])
  }

  return (
    <div>
      <TableContainer className={classes.container}>
        <Table stickyHeader aria-label="sticky table" className={classes.table}>
          <TableHead>
            <TableRow>
              {/* survey name header */}
              <TableCell
                key={'nameHeader'}
                className={clsx(
                  classes.cellContent,
                  classes.tableHeader,
                  classes.sticky,
                )}
              />

              {/* periods header */}
              {surveyPeriods?.map((period) => {
                const periodStatus = getSurveyGroupStatus(
                  classes,
                  surveysGroupedBySchedule[period],
                  timezone,
                )
                let periodName = period
                if (period.includes('followup_')) {
                  periodName =
                    'Session ' + parseInt(period.replace('followup_', '')) * 5
                  if (currentSite?.organization.name === 'StemWave') {
                    periodName =
                      'Session ' + parseInt(period.replace('followup_', ''))
                  }
                }
                return (
                  <TableCell
                    key={period}
                    className={clsx(classes.tableHeader, classes.cellContent)}
                  >
                    {periodName}
                    <br />
                    {periodStatus}
                  </TableCell>
                )
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {surveysGroupedBySlug ? (
              <>
                {tBodyRows.map((row) => {
                  return row
                })}
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={100}>
                  <Box p={3} width="100%" textAlign="center">
                    <Body1>No Survey records</Body1>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <SurveyAnswersModal
        open={openAnswersModal}
        setOpen={setOpenAnswerModal}
        setModalAnswers={setModalAnswers}
        survey={modalAnswers}
        timezone={timezone}
        isGroupView={isGroupView}
      />
    </div>
  )
}

export default SurveyTable
